import { appraisifyPDFTheme } from '@aloft/toolkit-sui';
import { Font, StyleSheet, Styles } from '@react-pdf/renderer';

import { PNG_WIDTH } from '../commentary/consts.ts';

const { colors, fontSizes } = appraisifyPDFTheme;

// Never split a word
Font.registerHyphenationCallback((word) => [word]);

const headerText: Styles[string] = {
  color: colors.text,
  fontFamily: 'Lato-Bold',
  fontWeight: 'bold',
  lineHeight: 'normal',
};

const body: Styles[string] = {
  color: colors.text,
  fontFamily: 'Lato-Regular',
  fontSize: fontSizes.md,
  fontWeight: 'normal',
  lineHeight: '150%',
};

export const fontStyles = StyleSheet.create({
  h1: {
    ...headerText,
    color: colors.background,
    fontSize: fontSizes.xxxl,
  },
  h3: {
    ...headerText,
    fontSize: fontSizes.xl,
  },
  h4: {
    ...headerText,
    fontSize: fontSizes.lg,
  },
  h4Light: {
    ...headerText,
    fontSize: fontSizes.lg,
    color: colors.textLight,
  },
  h5: {
    ...headerText,
    fontSize: fontSizes.md,
  },
  body,
  bodySpaced: {
    ...body,
    marginBottom: 6,
  },
  bodyRegularLight: {
    ...body,
    color: colors.textLight,
  },
  textLight: {
    color: colors.textLight,
  },
  uppercase: {
    textTransform: 'uppercase',
  },
  bodyLight: {
    ...body,
    color: colors.textLight,
    fontFamily: 'Lato-Light',
  },
  bodyItalic: {
    ...body,
    fontFamily: 'Lato-Italic',
  },
  bodyLarge: {
    ...body,
    fontSize: fontSizes.lg,
  },
  accent: {
    color: colors.textAccent,
  },
  small: {
    ...body,
    fontFamily: 'Lato-Light',
    fontSize: fontSizes.sm,
  },
  regularSmall: {
    fontSize: fontSizes.sm,
  },
  bodyBold: {
    ...body,
    fontFamily: 'Lato-Bold',
    fontWeight: 700,
  },
  listItem: {
    ...body,
    width: 20,
    textAlign: 'right',
    paddingRight: 4,
  },
  positive: {
    color: colors.positive,
  },
  negative: {
    color: colors.negative,
  },
});

export const pageStyles = StyleSheet.create({
  page: {
    display: 'flex',
    paddingHorizontal: 24,
    paddingVertical: 16,
    flexDirection: 'column',
    gap: 8,
  },
});

export const coverPageStyles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: colors.backgroundAccent,
    color: colors.background,
    height: '100%',
    position: 'relative',
  },
  header: {
    padding: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  inspectionCheckboxes: {
    flexDirection: 'column',
    gap: 8,
    marginRight: 8,
  },
  inspectionCheckboxSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inspectionCheckboxChecked: {
    fontSize: fontSizes.xs,
    backgroundColor: colors.backgroundAccentLight,
    borderRadius: 2,
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.backgroundAccent,
  },
  inspectionCheckboxUnchecked: {
    fontSize: fontSizes.xs,
    borderColor: colors.backgroundAccentLight,
    borderWidth: 2,
    borderRadius: 2,
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inspectionCheckboxText: {
    fontSize: fontSizes.md,
    color: colors.background,
  },
  headerRow: { flexDirection: 'row', alignItems: 'flex-end' },
  headerLogo: { width: 150, height: 40, objectFit: 'contain' },
  headerProductTypeTag: {
    fontSize: fontSizes.lg,
    color: colors.background,
    marginLeft: 10,
    backgroundColor: colors.backgroundAccentLight,
    padding: 4,
    borderRadius: 4,
  },
  subjectPropertyImage: {
    width: 350,
    objectFit: 'cover',
  },
  details: {
    padding: 16,
    backgroundColor: colors.background,
    color: colors.text,
    flex: 1,
    justifyContent: 'flex-start',
    gap: 4,
  },
  detail: { marginBottom: 4, gap: 4 },
  tags: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 12,
    color: colors.background,
    fontSize: fontSizes.sm,
  },
  tag: {
    marginLeft: 24,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  disclaimer: {
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    position: 'absolute',
    bottom: 24,
    right: 24,
    marginLeft: 36,
  },
  tagValue: {
    fontWeight: 'bold',
    backgroundColor: colors.backgroundAccentLight,
    padding: 4,
    borderRadius: 4,
  },
});

const badge: Styles[string] = {
  paddingHorizontal: 8,
  paddingVertical: 4,
  borderRadius: 2,
  fontSize: fontSizes.sm,
  fontFamily: 'Lato-Bold',
  textTransform: 'uppercase',
  alignSelf: 'flex-start',
  textAlign: 'center',
};

export const badgeStyles = StyleSheet.create({
  badge,
  compact: {
    ...badge,
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  aiv: {
    backgroundColor: appraisifyPDFTheme.colors.aivBackground,
    color: appraisifyPDFTheme.colors.text,
  },
  arv: {
    backgroundColor: appraisifyPDFTheme.colors.arvBackground,
    color: appraisifyPDFTheme.colors.background,
  },
});

export const tableStyles = StyleSheet.create({
  table: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
    backgroundColor: colors.strokeLight,
    border: `1px solid ${colors.strokeLight}`,
    flex: '0 0 auto',
    alignSelf: 'flex-start',
  },
  column: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  cell: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 20,
    backgroundColor: colors.background,
  },
  compactCell: {
    paddingHorizontal: 2,
    paddingVertical: 2,
    minHeight: 20,
    backgroundColor: colors.background,
  },
  text: {
    ...body,
    fontSize: fontSizes.sm,
  },
});

export const headerStyles = StyleSheet.create({
  header: {
    display: 'flex',
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 0,
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderBottomWidth: 1,
    borderBottomColor: appraisifyPDFTheme.colors.border,
  },
  titleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logoWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
  },
  logo: {
    width: 90,
    height: 30,
    objectFit: 'contain',
    paddingBottom: 8,
  },
});

export const propertyOrderDetailsPageStyles = StyleSheet.create({
  page: {
    padding: 32,
  },
  propertyImage: { width: 250, height: 250, objectFit: 'cover' },
  section: {
    flexDirection: 'row',
    gap: 12,
    paddingVertical: 10,
  },
  details: {
    flexDirection: 'column',
    flex: 1,
    gap: 4,
  },
  detail: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 3,
  },
  label: {
    width: 100,
    flexShrink: 0,
    paddingRight: 8,
  },
  value: {
    flex: 1,
    minWidth: 0,
  },
});

export const salesComparisonPageStyles = StyleSheet.create({
  content: {
    marginTop: 16,
    fontSize: fontSizes.xs,
  },
  gridColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '25%',
    flexGrow: 0,
    flexShrink: 0,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingRight: 8,
  },
  innerRow: {
    display: 'flex',
    flexDirection: 'row',
    flexBasis: '25%',
    flexGrow: 0,
  },
  header: {
    fontSize: fontSizes.sm,
    fontWeight: 'bold',
  },
  imageContainer: {
    height: 70,
    objectFit: 'cover',
    paddingTop: 4,
  },
  image: { width: '100%', height: '100%', objectFit: 'cover' },
  addressContainer: {
    backgroundColor: colors.backgroundAccentDark,
    color: colors.background,
  },
  address: {
    padding: 4,
    fontSize: fontSizes.sm,
  },
  label: {
    backgroundColor: colors.surface,
    padding: 3,
    border: `1px solid ${colors.border}`,
    borderTop: 0,
    borderRight: 0,
    flexGrow: 1,
    flexShrink: 1,
  },
  value: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 1,
    padding: 3,
    borderBottom: `1px solid ${colors.border}`,
    borderLeft: `1px solid ${colors.border}`,
    minHeight: 16,
  },
  negativeAdjustment: {
    color: colors.negative,
  },
  positiveAdjustment: {
    color: colors.positive,
  },
  text: {
    marginVertical: 'auto',
  },
  borderTop: {
    borderTop: `2px solid ${colors.border}`,
  },
  bold: {
    fontWeight: 'bold',
    fontSize: fontSizes.sm,
  },
  rowSpaceBetween: {
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  glossary: {
    marginTop: 6,
    paddingTop: 4,
    width: '100%',
  },
  glossaryText: {
    fontSize: fontSizes.xs,
    color: colors.textLight,
    fontFamily: 'Lato-Light',
    lineHeight: 1.35,
  },
});

export const salesComparisonMapPageStyles = StyleSheet.create({
  summaryContainer: {
    display: 'flex',
    flexDirection: 'row',
    paddingHorizontal: 0,
    paddingVertical: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 4,
    gap: 4,
  },
  summaryIcon: {
    width: 13,
    height: 16,
    objectFit: 'contain',
    padding: 2,
  },
  summaryItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mapContainer: {
    width: 550,
    height: 350,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
});

export const depreciatedCostAdjustmentsPageStyles = StyleSheet.create({
  mainDwellingCostBreakdownContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 32,
  },
  roundingAndThresholdsItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  roundingAndThresholdsItemTopRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  additionalStructure: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
});

const COST_APPROACH_PAGE_WIDTH_PT = 595;
const COST_APPROACH_PAGE_PADDING_HORIZONTAL_PT = 24;
const COST_APPROACH_TWO_COLUMN_GAP_PT = 12;
const COST_APPROACH_CONTENT_INNER_WIDTH_PT =
  COST_APPROACH_PAGE_WIDTH_PT - COST_APPROACH_PAGE_PADDING_HORIZONTAL_PT * 2;
const COST_APPROACH_CHART_COLUMN_WIDTH_PT =
  ((COST_APPROACH_CONTENT_INNER_WIDTH_PT - COST_APPROACH_TWO_COLUMN_GAP_PT) * 3) / (2 + 3);
const COST_APPROACH_EXPORT_CHART_INNER_PAD_H_PT = 12 * 2;
const COST_APPROACH_EXPORT_CHART_BORDER_H_PT = 2;
const COST_APPROACH_CHART_IMAGE_WIDTH_PT = Math.max(
  1,
  COST_APPROACH_CHART_COLUMN_WIDTH_PT -
    COST_APPROACH_EXPORT_CHART_INNER_PAD_H_PT -
    COST_APPROACH_EXPORT_CHART_BORDER_H_PT,
);
const COST_APPROACH_CHART_IMAGE_HEIGHT_PT = COST_APPROACH_CHART_IMAGE_WIDTH_PT * (350 / PNG_WIDTH);

const costApproachIndicatedValueRowBg = '#EDF0FF';

export const costApproachPageStyles = StyleSheet.create({
  twoColumnRow: {
    flexDirection: 'row',
    gap: COST_APPROACH_TWO_COLUMN_GAP_PT,
    alignItems: 'flex-start',
    width: '100%',
  },
  tableColumn: {
    flexGrow: 2,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  chartColumn: {
    flexGrow: 3,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    flexDirection: 'column',
    gap: 8,
    alignItems: 'stretch',
  },
  chartBlock: {
    width: '100%',
    flexShrink: 0,
    alignSelf: 'stretch',
  },
  chartImage: {
    width: COST_APPROACH_CHART_IMAGE_WIDTH_PT,
    height: COST_APPROACH_CHART_IMAGE_HEIGHT_PT,
    objectFit: 'contain',
  },
  rollupLabelBold: {
    color: colors.text,
    fontFamily: 'Lato-Bold',
    fontWeight: 700,
    fontSize: fontSizes.sm,
    lineHeight: 1.2,
  },
  tableBody: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.border,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingLeft: 8,
    paddingRight: 8,
    marginBottom: 4,
  },
  tableHeaderLabelCol: {
    flex: 1.55,
    justifyContent: 'center',
  },
  tableHeaderBadgeCol: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  tableHeaderValueText: {
    textAlign: 'right',
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    minHeight: 22,
  },
  rowLast: {
    borderBottomWidth: 0,
  },
  rowIndicatedValue: {
    backgroundColor: costApproachIndicatedValueRowBg,
  },
  labelCell: {
    flex: 1.55,
    paddingVertical: 4,
    paddingLeft: 8,
    paddingRight: 4,
    justifyContent: 'center',
  },
  labelCellIndent: {
    paddingLeft: 20,
  },
  valueCell: {
    flex: 1,
    paddingVertical: 4,
    paddingHorizontal: 6,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  methodologySection: {
    marginTop: 12,
    gap: 8,
  },
});

export const commentarySectionStyles = StyleSheet.create({
  multiCommentarySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  titleContainer: {
    marginBottom: 4,
  },
});

export const certificationPageStyles = StyleSheet.create({
  text: {
    fontSize: fontSizes.lg,
    color: colors.text,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  title: {
    fontSize: fontSizes.xl,
    color: colors.text,
    paddingTop: 8,
    paddingBottom: 12,
    fontWeight: 'bold',
  },
  signatureSection: {
    marginTop: 8,
  },
  signatureContainer: {
    padding: 15,
    borderWidth: 1,
    borderRadius: 5,
    borderStyle: 'solid',
    borderColor: colors.border,
    width: '100%',
    gap: 12,
    marginTop: 8,
  },
  commentarySection: {
    marginTop: 8,
  },
  signatureSectionFieldsContainer: {
    flexDirection: 'row',
  },
  signatureSectionLabel: {
    flex: 1,
    fontSize: fontSizes.lg,
    color: colors.textLight,
  },
  signatureSectionField: {
    flex: 1,
    fontSize: fontSizes.lg,
    color: colors.text,
  },
  signatureSectionImageContainer: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  signatureImage: {
    height: 40,
    width: 150,
    objectFit: 'contain',
  },
  stateSealImage: {
    width: 50,
    height: 50,
    objectFit: 'contain',
  },
  bulletPoint: {
    width: 15,
    marginRight: 10,
    justifyContent: 'flex-end',
    flexDirection: 'row',
  },
  listElement: {
    flexDirection: 'row',
  },
  certificationStatementPageContainer: {
    width: '100%',
    marginTop: 5,
    paddingRight: 30,
  },
  certificationStatementText: {
    fontSize: fontSizes.lg,
    color: colors.text,
    fontWeight: 400,
    lineHeight: 1.15,
  },
  certificationStatementTitleContainer: {
    marginBottom: 8,
  },
});

export const gridStyles = StyleSheet.create({
  gridContainer: {
    display: 'flex',
    border: `1px solid ${colors.border}`,
    borderBottom: 0,
  },
  cardContainer: {
    display: 'flex',
    border: `1px solid ${colors.border}`,
    borderRadius: 4,
  },
  cardContainerMuted: {
    display: 'flex',
    border: `1px solid ${colors.border}`,
    borderRadius: 4,
    backgroundColor: colors.surfaceLight,
  },
  gridRow: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    borderBottom: `1px solid ${colors.border}`,
  },
  gridCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  gridCell: {
    display: 'flex',
    paddingVertical: 4,
    paddingLeft: 8,
    alignItems: 'flex-start',
    borderRight: `1px solid ${colors.border}`,
    flex: 1,
  },
  listItemDotSmall: {
    height: '3px',
    width: '3px',
    borderRadius: '1.5px',
    backgroundColor: colors.text,
    display: 'flex',
    alignSelf: 'center',
  },
  compactRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  gridSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  gridSection70: {
    width: '70%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  verticalSpace: {
    width: 1,
    height: 8,
  },
  photoGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
});

export const reconciliationPageStyles = StyleSheet.create({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 4,
  },
});

export const summaryPageStyles = StyleSheet.create({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 4,
  },
});
export const priorSalesPageStyles = StyleSheet.create({
  propertyBlock: {
    marginBottom: 10,
  },
});
